import { Link } from "react-router-dom";
import { X, Check, Sparkles, Lock, TrendingUp, Brain, Users, Target } from "lucide-react";

const TIER_DETAILS = {
  group: {
    color: "#007AFF",
    label: "Group sessions",
    tag: "From £8 per player",
    accentBg: "rgba(0,122,255,0.08)",
    short: "Small-group coaching — community, fast progress, low cost.",
    pillars: [
      { icon: Users, text: "Capped at 6 players · ranked by ability" },
      { icon: Target, text: "Drills, point play, structured games" },
      { icon: Sparkles, text: "Self-rating tracks YOUR own perception" },
    ],
    includes: [
      "Coach-led drills (60–90 min)",
      "Skill-matched groups so you're not the weakest or strongest",
      "Self-rating dashboard (DUPR + 10 fundamentals)",
      "Group banter, community, friendships",
    ],
    notIncluded: [
      { title: "Coach-rated progress tracking", reason: "Group sessions don't give the coach time to rate every player individually." },
      { title: "AI-generated 4-week reports", reason: "Personalised reports require 1:1 or Performance data — they're not generated from group attendance." },
      { title: "Skill-radar graphs over time", reason: "Only available once you've attended a paid Performance or 1:1 session — see below." },
    ],
    activationNote: null,
    cta: "Browse group sessions",
    ctaTo: "/register",
  },
  performance: {
    color: "#FF4500",
    label: "Performance programme",
    tag: "Coach-defined package",
    accentBg: "rgba(255,69,0,0.08)",
    short: "Pre-paid programme. A set number of sessions over a flexible window, with full progress tracking.",
    pillars: [
      { icon: TrendingUp, text: "Coach-rated skill graphs over time" },
      { icon: Brain, text: "AI report every 4 weeks tracking your trajectory" },
      { icon: Target, text: "Personal session focus picked from your weakest skills" },
    ],
    includes: [
      "Weekly 1:1 with your dedicated coach",
      "Access to all group sessions",
      "Coach-rated progress across the 10 pickleball fundamentals",
      "AI-generated 4-week trajectory reports",
      "Next-session focus picked from your weakest skills",
      "Full skill-radar history + graphs",
    ],
    notIncluded: [],
    activationNote: "Full performance tracking + AI reports activate the moment you book, pay for, and attend your first Performance session.",
    cta: "Apply for Performance",
    ctaTo: "/register",
  },
  one_on_one: {
    color: "#CCFF00",
    label: "1:1 coaching",
    tag: "From £30 per hour",
    accentBg: "rgba(204,255,0,0.08)",
    short: "Pure private coaching. Pace, plan and focus are 100% yours.",
    pillars: [
      { icon: Target, text: "Every minute is on your weakest skill" },
      { icon: TrendingUp, text: "Coach-rated skill graphs over time" },
      { icon: Brain, text: "AI report every 4 weeks" },
    ],
    includes: [
      "Pay-as-you-go hourly 1:1 sessions",
      "Coach-rated progress across the 10 pickleball fundamentals",
      "AI-generated 4-week trajectory reports",
      "Full skill-radar history + graphs",
      "Direct chat with your coach between sessions",
    ],
    notIncluded: [],
    activationNote: "Full progress tracking + AI reports activate the moment you book, pay for, and attend your first 1:1 session.",
    cta: "Book a 1:1",
    ctaTo: "/register",
  },
};

export default function TierDetailsModal({ tierId, onClose }) {
  if (!tierId) return null;
  const t = TIER_DETAILS[tierId];
  if (!t) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="tier-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <div
        className="relative card-surface max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        style={{ borderTop: `4px solid ${t.color}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="tier-modal-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-[#262626] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8" style={{ background: t.accentBg }}>
          <div className="label-mono mb-2" style={{ color: t.color }}>{tierId.replace("_", " ").toUpperCase()}</div>
          <h2 className="h-display text-4xl tracking-tighter mb-2" data-testid="tier-modal-title">{t.label}</h2>
          <div className="label-mono mb-4">{t.tag}</div>
          <p className="text-[#E5E5E5] leading-relaxed max-w-xl">{t.short}</p>
        </div>

        <div className="p-8 space-y-8">
          {/* Pillars */}
          <div>
            <div className="label-mono mb-4">WHAT YOU GET</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {t.pillars.map((p, i) => (
                <div key={i} className="border border-[#262626] p-3">
                  <div className="w-8 h-8 mb-2 flex items-center justify-center" style={{ background: t.color }}>
                    <p.icon strokeWidth={2.5} className="w-4 h-4 text-black" />
                  </div>
                  <div className="text-xs text-[#E5E5E5] leading-snug">{p.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Includes */}
          <div>
            <div className="label-mono mb-3">INCLUDED</div>
            <ul className="space-y-2">
              {t.includes.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#E5E5E5]">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.color }} strokeWidth={2.5} />
                  {line}
                </li>
              ))}
            </ul>
          </div>

          {/* NOT included (Group only) */}
          {t.notIncluded.length > 0 && (
            <div className="card-surface p-4 border-l-4 border-[#FF4500]" data-testid="tier-modal-restrictions">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-[#FF4500]" />
                <div className="label-mono" style={{ color: "#FF4500" }}>NOT INCLUDED IN THIS TIER</div>
              </div>
              <ul className="space-y-3">
                {t.notIncluded.map((n, i) => (
                  <li key={i}>
                    <div className="text-sm font-semibold text-white mb-0.5">{n.title}</div>
                    <div className="text-xs text-[#A3A3A3]">{n.reason}</div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-3 border-t border-[#262626] text-xs text-[#A3A3A3]">
                Want all of this? Book any <span className="text-[#FF4500] font-bold">Performance</span> or <span className="text-[#CCFF00] font-bold">1:1</span> session — once you attend a paid one, full tracking + AI reports unlock on your profile.
              </div>
            </div>
          )}

          {/* Activation note (Performance / 1:1) */}
          {t.activationNote && (
            <div
              data-testid="tier-modal-activation"
              className="border-l-4 p-4 flex items-start gap-3"
              style={{ borderColor: t.color, background: t.accentBg }}
            >
              <Sparkles className="w-5 h-5 shrink-0 mt-0.5" style={{ color: t.color }} />
              <div className="text-sm text-[#E5E5E5] leading-relaxed">
                <strong style={{ color: t.color }}>How activation works.</strong> {t.activationNote}
              </div>
            </div>
          )}

          {/* CTA */}
          <Link
            to={t.ctaTo}
            onClick={onClose}
            data-testid="tier-modal-cta"
            className="block w-full text-center py-4 text-sm font-bold uppercase tracking-wider transition-transform hover:translate-y-[-1px]"
            style={{ background: t.color, color: "#0A0A0A" }}
          >
            {t.cta} →
          </Link>
        </div>
      </div>
    </div>
  );
}
